import * as Dialog from '@radix-ui/react-dialog';
import { ArrowLeft, X } from 'lucide-react';
import { memo, useCallback, useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ChoiceSelect } from '@/components/ui/choice-select';
import { Input } from '@/components/ui/input';
import { useNarrow } from '@/components/list/use-narrow';
import { cn } from '@/lib/utils';
import { countryOptions } from '@/profile/countries';
import {
  addName,
  addSite,
  createSupplier,
  siteBySign,
  type Supplier,
  type SupplierCreate,
  type SupplierPick,
} from './api';
import { AddressField } from '@/components/address/address-field';
import { isAddressEmpty, type AddressValue } from '@/components/address/address-value';
import { addressOf, addressValueOf } from '../address';
import { SiteTypeFields, type SiteTypeValue } from '../types';
import { splitVat, suggestedCurrency, VIES_COUNTRIES } from './countries';
import { DuplicateNotice } from './DuplicateNotice';
import { SimilarNotice } from './SimilarNotice';
import { existingOf, VatField, type VatState } from './VatField';

/** What the window ends with: a supplier made or completed here, or one already in the catalog. */
export type CreateResult =
  | { readonly kind: 'supplier'; readonly supplier: Supplier; readonly siteId: string | null }
  | { readonly kind: 'pick'; readonly pick: SupplierPick };

export type CreateInitial = {
  readonly name: string;
  /** The document's VAT number, as read (board 1.4: the window opens already filled). */
  readonly vat?: string;
};

type Draft = {
  readonly name: string;
  readonly country: string;
  readonly vat: string;
  readonly office: AddressValue;
  readonly differentSite: boolean;
  readonly sign: string;
  readonly type: SiteTypeValue;
  readonly siteAddress: AddressValue;
};

function draftOf(initial: CreateInitial, stay: boolean): Draft {
  const typed = initial.vat?.trim() ?? '';
  const prefix = splitVat(typed, 'IT')?.vatCountry;
  const country =
    prefix && (VIES_COUNTRIES.has(prefix) || prefix === 'GB' || prefix === 'CH') ? prefix : 'IT';
  return {
    name: initial.name.trim(),
    country,
    vat: typed,
    office: addressValueOf(null, country),
    differentSite: false,
    sign: initial.name.trim(),
    type: stay ? { macro: 'lodging', detail: 'hotel' } : { macro: '', detail: '' },
    siteAddress: addressValueOf(null, country),
  };
}

/** A change to the draft; an address not written yet follows the supplier's country. */
function merge(prev: Draft, patch: Partial<Draft>): Draft {
  const next = { ...prev, ...patch };
  const country = patch.country;
  if (country === undefined || country === prev.country) return next;
  const follow = (address: AddressValue) =>
    isAddressEmpty(address) ? { ...address, countryCode: country } : address;
  return { ...next, office: follow(next.office), siteAddress: follow(next.siteAddress) };
}

/** The request: only what was filled; the site of use only when it differs (or for a property). */
function bodyOf(draft: Draft, vat: VatState, stay: boolean, acknowledgeSimilar: boolean) {
  const split = splitVat(draft.vat, draft.country);
  const verifiedName =
    vat.kind === 'checked' && vat.preview.identifierCheck === 'verified' ? vat.preview.name : null;
  const siteAddress = addressOf(draft.siteAddress);
  const office = addressOf(draft.office);
  const site = {
    sign: (stay ? draft.name : draft.sign).trim() || null,
    macro: draft.type.macro || null,
    detail: draft.type.detail || null,
    ...(siteAddress ? { address: siteAddress } : {}),
  };
  const body: SupplierCreate = {
    name: draft.name.trim(),
    country: draft.country,
    acknowledgeSimilar,
    ...(split ? { vatCountry: split.vatCountry, vat: split.vat } : {}),
    ...(verifiedName ? { legalName: verifiedName } : {}),
    ...(suggestedCurrency(draft.country)
      ? { usualCurrency: suggestedCurrency(draft.country) }
      : {}),
    ...(!stay && office ? { registeredOffice: office } : {}),
    ...(stay || draft.differentSite ? { site } : {}),
  };
  return { body, site, office };
}

type Failure = 'failed' | 'forbidden' | 'invalid' | null;

/**
 * «Nuovo fornitore» (boards 1.5–1.8, 1.11; report §3.2): opened from «Crea il fornitore «…»», from
 * the document's data (1.4) or, with `stay`, for a property in «Dove hai dormito?» (1.9). Every
 * field but the name is optional. The supplier is created only on «Crea il fornitore» (D246);
 * closing the window or «Torna alla ricerca» creates nothing. A centred dialog on wider screens, a
 * full page on the phone with the buttons above the keyboard.
 */
export function CreateSupplierDialog({
  initial,
  stay = false,
  onDone,
  onClose,
}: {
  readonly initial: CreateInitial;
  readonly stay?: boolean;
  readonly onDone: (result: CreateResult) => void;
  readonly onClose: () => void;
}) {
  const { t, i18n } = useTranslation('suppliers');
  const narrow = useNarrow();
  const uid = useId();
  const [draft, setDraft] = useState<Draft>(() => draftOf(initial, stay));
  const [vat, setVat] = useState<VatState>({ kind: 'empty' });
  const [similar, setSimilar] = useState<readonly SupplierPick[] | null>(null);
  const [conflict, setConflict] = useState<SupplierPick | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure>(null);
  const [dupFailed, setDupFailed] = useState(false);
  const countries = useMemo(
    () => countryOptions(i18n.language).map((c) => ({ value: c.code, label: c.name })),
    [i18n.language],
  );
  const set = (patch: Partial<Draft>): void => setDraft((prev) => merge(prev, patch));
  const onCountry = useCallback((country: string) => {
    setDraft((prev) => merge(prev, { country }));
    setConflict(null);
  }, []);

  const existing = existingOf(vat) ?? conflict;
  const nameMissing = draft.name.trim() === '';
  const blocked = existing !== null || vat.kind === 'form-invalid';
  const title = nameMissing
    ? t('picker.create.titleEmpty')
    : t(stay ? 'picker.create.titleStay' : 'picker.create.title', { name: draft.name.trim() });

  const submit = async (): Promise<void> => {
    if (nameMissing || blocked || busy) return;
    setBusy(true);
    setFailure(null);
    const { body } = bodyOf(draft, vat, stay, similar !== null);
    const outcome = await createSupplier(body);
    setBusy(false);
    if (outcome.kind === 'created') {
      const siteId =
        stay || draft.differentSite ? siteBySign(outcome.supplier, body.site?.sign ?? '') : null;
      onDone({ kind: 'supplier', supplier: outcome.supplier, siteId });
    } else if (outcome.kind === 'vat-exists') {
      setConflict(outcome.existing);
    } else if (outcome.kind === 'similar') {
      setSimilar(outcome.similar);
    } else if (outcome.problem.status === 403) {
      setFailure('forbidden');
    } else if (outcome.problem.status === 422) {
      setFailure('invalid');
    } else {
      setFailure('failed');
    }
  };

  /** Board 1.6: the typed name becomes a site of the supplier that has the number. */
  const addAsSite = async (target: SupplierPick): Promise<void> => {
    setBusy(true);
    setDupFailed(false);
    const { site, office } = bodyOf({ ...draft, sign: draft.name }, vat, true, false);
    const address = site.address ?? office ?? undefined;
    const outcome = await addSite(target.id, { ...site, ...(address ? { address } : {}) });
    setBusy(false);
    if (!outcome.ok) {
      setDupFailed(true);
      return;
    }
    onDone({
      kind: 'supplier',
      supplier: outcome.value,
      siteId: siteBySign(outcome.value, draft.name.trim()),
    });
  };

  const addAsName = async (target: SupplierPick): Promise<void> => {
    setBusy(true);
    setDupFailed(false);
    const outcome = await addName(target.id, draft.name.trim());
    setBusy(false);
    if (!outcome.ok) {
      setDupFailed(true);
      return;
    }
    onDone({ kind: 'supplier', supplier: outcome.supplier, siteId: null });
  };

  const top = existing ? (
    <DuplicateNotice
      existing={existing}
      typedName={draft.name}
      busy={busy}
      failed={dupFailed}
      onUse={() => onDone({ kind: 'pick', pick: existing })}
      onAddSite={() => void addAsSite(existing)}
      onAddName={() => void addAsName(existing)}
    />
  ) : similar && similar.length > 0 ? (
    <SimilarNotice similar={similar} onUse={(pick) => onDone({ kind: 'pick', pick })} />
  ) : null;

  const why = existing
    ? t('picker.create.blocked')
    : similar
      ? t('picker.create.keepRest')
      : suggestedCurrency(draft.country) && draft.country !== 'IT'
        ? t('picker.create.currency', { currency: suggestedCurrency(draft.country) })
        : t('picker.create.later');

  const foot = (
    <>
      {!narrow && <span className="text-muted-foreground mr-auto text-xs">{why}</span>}
      {failure && (
        <span role="alert" className="text-destructive basis-full text-sm">
          {t(`picker.create.${failure}`)}
        </span>
      )}
      <Button type="button" variant="outline" className="min-h-11" onClick={onClose}>
        {t('picker.create.back')}
      </Button>
      <Button
        type="submit"
        form={`${uid}-form`}
        className="min-h-11"
        disabled={nameMissing || blocked || busy}
      >
        {busy
          ? t('picker.create.creating')
          : similar
            ? t('picker.create.submitAnyway')
            : t(stay ? 'picker.create.submitStay' : 'picker.create.submit')}
      </Button>
    </>
  );

  return (
    <Dialog.Root open onOpenChange={(open) => (open ? undefined : onClose())}>
      <Dialog.Portal>
        {!narrow && <Dialog.Overlay className="fixed inset-0 z-50 bg-[var(--scrim)]" />}
        <Dialog.Content
          className={cn(
            'bg-card text-foreground fixed z-50 flex flex-col',
            narrow
              ? 'inset-0'
              : 'border-border top-[5vh] left-1/2 max-h-[90vh] w-[min(42.5rem,calc(100vw-2rem))] -translate-x-1/2 rounded-xl border shadow-lg',
          )}
        >
          <header className="border-border flex items-start gap-3 border-b p-4">
            {narrow && (
              <Dialog.Close asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="size-11 p-0"
                  aria-label={t('picker.create.back')}
                >
                  <ArrowLeft aria-hidden="true" />
                </Button>
              </Dialog.Close>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <Dialog.Title className="text-lg font-semibold">{title}</Dialog.Title>
              <Dialog.Description className="text-muted-foreground text-sm">
                {narrow ? t('picker.create.textPhone') : t('picker.create.text')}
              </Dialog.Description>
            </div>
            {!narrow && (
              <Dialog.Close asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="size-11 p-0"
                  aria-label={t('picker.create.close')}
                >
                  <X aria-hidden="true" />
                </Button>
              </Dialog.Close>
            )}
          </header>
          <form
            id={`${uid}-form`}
            noValidate
            className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            {top}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                id={`${uid}-name`}
                label={t('picker.create.name')}
                hint={stay ? undefined : t('picker.create.nameHint')}
                className="sm:col-span-2"
              >
                <Input
                  id={`${uid}-name`}
                  className="min-h-11"
                  value={draft.name}
                  aria-required
                  aria-invalid={nameMissing || undefined}
                  aria-describedby={stay ? undefined : `${uid}-name-hint`}
                  onChange={(event) => {
                    set({ name: event.target.value });
                    setSimilar(null);
                  }}
                />
              </Field>
              <Field id={`${uid}-country`} label={t('picker.create.country')}>
                <CountrySelect
                  id={`${uid}-country`}
                  value={draft.country}
                  choices={countries}
                  onChange={onCountry}
                />
              </Field>
              <VatField
                id={`${uid}-vat`}
                country={draft.country}
                value={draft.vat}
                onChange={(value) => {
                  const prefix = splitVat(value, draft.country)?.vatCountry;
                  set({
                    vat: value,
                    // A typed prefix of another country moves the country with it («ES B12…»).
                    ...(prefix &&
                    prefix !== draft.country &&
                    (VIES_COUNTRIES.has(prefix) || prefix === 'GB')
                      ? { country: prefix }
                      : {}),
                  });
                  setConflict(null);
                }}
                state={vat}
                onState={setVat}
              />
              {stay ? (
                <div className="sm:col-span-2">
                  <AddressField
                    label={t('picker.create.siteAddress')}
                    labelNote={t('picker.create.optionalMasculine')}
                    value={draft.siteAddress}
                    onChange={(siteAddress) => set({ siteAddress })}
                  />
                </div>
              ) : (
                <>
                  <div className="sm:col-span-2">
                    <AddressField
                      label={t('picker.create.office')}
                      labelNote={t('picker.create.optional')}
                      value={draft.office}
                      onChange={(office) => set({ office })}
                    />
                  </div>
                  <SiteOfUse uid={uid} draft={draft} set={set} />
                </>
              )}
            </div>
          </form>
          <footer className="border-border flex flex-wrap items-center justify-end gap-2 border-t p-4">
            {foot}
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * «Paese», kept out of the window's re-renders: Radix Select renders all its ~250 options into a
 * hidden fragment even while closed, so re-rendering it on every keystroke in the other fields made
 * typing slow (and the tests time out under load). It renders again only when its props change.
 */
const CountrySelect = memo(function CountrySelect({
  id,
  value,
  choices,
  onChange,
}: {
  readonly id: string;
  readonly value: string;
  readonly choices: readonly { readonly value: string; readonly label: string }[];
  readonly onChange: (country: string) => void;
}) {
  return (
    <ChoiceSelect
      triggerClassName="data-[size=default]:min-h-11"
      position="popper"
      id={id}
      value={value}
      choices={choices}
      onChange={onChange}
    />
  );
});

/** «Il posto in cui l'hai usato è diverso dalla sede legale», and its sign, type and address. */
function SiteOfUse({
  uid,
  draft,
  set,
}: {
  readonly uid: string;
  readonly draft: Draft;
  readonly set: (patch: Partial<Draft>) => void;
}) {
  const { t } = useTranslation('suppliers');
  return (
    <div className="border-border bg-background flex flex-col gap-3 rounded-lg border p-3 sm:col-span-2">
      <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm font-semibold">
        <Checkbox
          checked={draft.differentSite}
          onChange={(event) => set({ differentSite: event.target.checked })}
        />
        {t('picker.create.differentSite')}
      </label>
      {draft.differentSite && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field id={`${uid}-sign`} label={t('picker.create.sign')}>
            <Input
              id={`${uid}-sign`}
              className="min-h-11"
              value={draft.sign}
              onChange={(event) => set({ sign: event.target.value })}
            />
          </Field>
          <SiteTypeFields
            value={draft.type}
            onChange={(type) => set({ type })}
            triggerClassName="data-[size=default]:min-h-11"
            field={(key, label, control) => (
              <Field key={key} id={`${uid}-${key}`} label={label}>
                {control(`${uid}-${key}`)}
              </Field>
            )}
          />
          <div className="sm:col-span-2">
            <AddressField
              label={t('picker.create.siteAddress')}
              value={draft.siteAddress}
              onChange={(siteAddress) => set({ siteAddress })}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function Field({
  id,
  label,
  optional = false,
  hint,
  className,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly optional?: boolean;
  readonly hint?: string | undefined;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  const { t } = useTranslation('suppliers');
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-semibold">
        {label}
        {optional && (
          <span className="text-muted-foreground font-normal"> {t('picker.create.optional')}</span>
        )}
      </label>
      {children}
      {hint && (
        <small id={`${id}-hint`} className="text-muted-foreground text-xs">
          {hint}
        </small>
      )}
    </div>
  );
}
